import React, { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Switch,
  Chip,
  Typography,
  useTheme,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import Modal from '..';
import makeStyles from '@mui/styles/makeStyles';
import { useCashflowTemplates } from '@/hooks/useCashflowTemplates';
import Grid from '@mui/material/Grid2';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { MODEL_TYPES } from '@/utils/cashflowConstants';
import { styles as portfolioModelStyles } from './styles';

const useStyles = makeStyles(() => ({
  modal: {
    width: '90%',
    maxWidth: '575px',
    height: 'auto',
    maxHeight: '90vh',
    backgroundColor: '#fff',
  },
}));

const TemplateSelectionList = ({
  consolidatedLiveModelsAdmin,
  selectedTemplateId,
  setSelectedTemplateId,
  loadingTemplates,
  templates,
  styles,
}) => {
  if (!consolidatedLiveModelsAdmin) {
    return null;
  }

  return (
    <Box sx={styles.templateListScrollContainer} flexGrow={1}>
      <Box>
        <Typography variant="subtitle2" sx={styles.templateListTitle}>
          Available Templates
        </Typography>
        <Box
          onClick={() => setSelectedTemplateId(null)}
          sx={{
            ...styles.templateItem,
            ...(selectedTemplateId === null ? styles.templateItemSelected : {}),
          }}
          data-testid="template-item-none"
        >
          <Typography variant="subtitle1" fontWeight="bold">
            None
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Do not use a saved template
          </Typography>
        </Box>
        {loadingTemplates ? (
          <Typography>Loading templates...</Typography>
        ) : templates?.length > 0 ? (
          templates.map((template) => (
            <Box
              key={template.id}
              onClick={() => setSelectedTemplateId(template.id)}
              sx={{
                ...styles.templateItem,
                ...(selectedTemplateId === template.id ? styles.templateItemSelected : {}),
              }}
              data-testid={`template-item-${template.id}`}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {template.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Created by{' '}
                  {template.createdBy
                    ? `${template.createdBy.firstName} ${template.createdBy.lastName}`
                    : 'Unknown'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {template.createdAt ? new Date(template.createdAt).toLocaleDateString() : ''}
                </Typography>
              </Box>
              <Chip
                label={template.isPublished ? 'Published' : 'Draft'}
                color={template.isPublished ? 'success' : 'default'}
                size="small"
                sx={{ ml: 2 }}
              />
            </Box>
          ))
        ) : (
          <Typography>No templates available.</Typography>
        )}
      </Box>
    </Box>
  );
};

export const PortfolioModel = ({ open, setOpen, onRequestReport, portfolios }) => {
  const { consolidatedLiveModelsAdmin } = useFeatureFlags();
  const [emailReport, setEmailReport] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [reportName, setReportName] = useState('');
  const modalStyle = useStyles();
  const theme = useTheme();
  const styles = portfolioModelStyles(theme, { showTemplateDropdown: consolidatedLiveModelsAdmin });
  const reportNameId = 'portfolio-model-report-name';

  const { templates, loadingTemplates } = useCashflowTemplates(MODEL_TYPES.clm.typeName);

  const extractUniqueNamesFromPortfolios = (filterKey, propertyName = 'name') => {
    return [
      ...new Set(
        portfolios.flatMap((portfolio) =>
          (portfolio.filters[filterKey] || []).map((item) => item[propertyName]),
        ),
      ),
    ];
  };
  const companyNames = extractUniqueNamesFromPortfolios('companies');
  const assetNames = extractUniqueNamesFromPortfolios('assets');
  const assetUnitNames = extractUniqueNamesFromPortfolios('assetUnits', 'optionDisplayName');

  const clear = () => {
    setEmailReport(false);
    setSelectedTemplateId(null);
    setReportName('');
  };

  const handleClose = () => {
    clear();
    setOpen(false);
  };

  const handleRequestReport = () => {
    onRequestReport({
      modelType: MODEL_TYPES.clm.typeName,
      templateId: selectedTemplateId,
      writeFlag: false,
      emailReport,
      reportName,
    });
    handleClose();
  };

  return (
    <Modal
      handleClose={handleClose}
      open={open}
      title="PORTFOLIO MODEL"
      titleAlignedWithClose={true}
      styles={modalStyle}
    >
      <Box sx={styles.modalContentWrapper}>
        <Divider />
        <Box mb={2} px={2} mt={2}>
          {companyNames.length > 0 && (
            <Box mb={1}>
              <Typography variant="subtitle2">Companies</Typography>
              <Typography variant="body2">{companyNames.join(', ')}</Typography>
            </Box>
          )}
          {assetNames.length > 0 && (
            <Box mb={1}>
              <Typography variant="subtitle2">Assets</Typography>
              <Typography variant="body2">{assetNames.join(', ')}</Typography>
            </Box>
          )}
          {assetUnitNames.length > 0 && (
            <Box>
              <Typography variant="subtitle2">Asset Units</Typography>
              <Typography variant="body2">{assetUnitNames.join(', ')}</Typography>
            </Box>
          )}
        </Box>
        <Grid>
          <Box mb={3} px={2} sx={{ gridColumn: 'span 3' }}>
            <Typography sx={styles.label} variant="overline">
              Customize File Name
            </Typography>
            <TextField
              id={reportNameId}
              variant="outlined"
              size="small"
              fullWidth
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              sx={{ mt: 1 }}
            />
          </Box>
          <Box
            sx={{
              gridColumn: 'span 4',
            }}
          />
        </Grid>
        <TemplateSelectionList
          consolidatedLiveModelsAdmin={consolidatedLiveModelsAdmin}
          selectedTemplateId={selectedTemplateId}
          setSelectedTemplateId={setSelectedTemplateId}
          loadingTemplates={loadingTemplates}
          templates={templates}
          styles={styles}
        />
        <div style={styles.spaceBetween}>
          <div style={styles.formControlRow}>
            <FormControlLabel
              control={
                <Switch checked={emailReport} onChange={() => setEmailReport(!emailReport)} />
              }
              label="Email Report"
            />
          </div>
          <div style={styles.formControlRow}>
            <Box sx={{ ...styles.buttonsContainer, ...styles.boxFlex }}>
              <Button
                onClick={() => handleRequestReport(true)}
                sx={styles.button}
                variant="contained"
              >
                Run Report
              </Button>
            </Box>
          </div>
        </div>
      </Box>
    </Modal>
  );
};
